import { useState, useEffect, useRef, memo } from "react";
import { useContext, useMemo } from "react";
import LevelTransmitterIndicator from "../components/LevelTransmitterIndicator";
import Transmitter from "../components/Transmitter";
import ControlValveControlPanel from "../components/ControlValveControlPanel";
import Valve from "../components/Valve";
import "./PostDesalterHEX1.css";
import { WellContext } from "../context/WellContext";
import { AlarmContext } from "../context/AlarmContext";
import { PumpContext } from "../context/PumpContext";
import { ProcessContext } from "../context/ProcessContext";
import Pump from "../components/Pump";
import PumpFaceplate from "../components/PumpFaceplate";
import MDSFaceplate from "../components/MDSFaceplate";
import { PlantStore } from "../engine/PlantStore";
import { usePlantStore } from "../engine/usePlantStore";
import { trackPageVisit } from "../competency/PageTracker";

import TransmitterFaceplate from "../components/TransmitterFaceplate";





function ValveOutput({
    tag,
    value,
    x,
    y,
    r = 0,
    description = "Control Valve Opening"
}) {

    const [hover, setHover] = useState(false);
    const [showFaceplate, setShowFaceplate] = useState(false);

    return (
        <>
            {/* Hover */}
            {hover && (
                <div
                    style={{
                        position: "absolute",
                        left: x + 30,
                        top: y - 5,
                        background: "rgba(0,80,180,.85)",
                        color: "#fff",
                        padding: "5px 8px",
                        borderRadius: "4px",
                        border: "1px solid #4fc3ff",
                        fontSize: "11px",
                        zIndex: 9999,
                        pointerEvents: "none",
                        whiteSpace: "nowrap"
                    }}
                >
                    <strong>{tag}</strong><br />
                    {description}
                </div>
            )}

            {/* Original Valve Output */}
            <div
                onMouseEnter={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
                onClick={() => setShowFaceplate(true)}
                style={{
                    position: "absolute",
                    left: x,
                    top: y,

                    color: "lime",
                    background: "black",

                    fontSize: "10px",
                    padding: "1px 4px",

                    borderRadius: "4px",
                    border: "1px solid #555",

                    transform: `rotate(${r}deg)`,
                    transformOrigin: "center",

                    cursor: "pointer",

                    fontWeight: "bold",

                    minWidth: "22px",
                    textAlign: "center",

                    transition: "0.2s",
                    boxShadow: hover
                        ? "0 0 8px rgba(0,255,255,.7)"
                        : "none"
                }}
            >
                {Number(value).toFixed(1)} %
            </div>

            {showFaceplate && (
                <TransmitterFaceplate
                    tag={tag}
                    value={Number(value)}
                    unit="%"
                    min={0}
                    max={100}
                    HH={100}
                    H={90}
                    L={10}
                    LL={0}
                    description={description}
                    x={x + 35}
                    y={y + 20}
                    onClose={() => setShowFaceplate(false)}
                />
            )}
        </>
    );
}


function PostDesalterHEX1() {
  // ✅ Use ref to track render count
  const renderCount = useRef(0);
  renderCount.current += 1;
  console.log(`PostDesalterHEX1 Render: ${renderCount.current}`);

  // ✅ Track page visit only once
  useEffect(() => {
    trackPageVisit("PostDesalterHEX1");
  }, []);

  const {processData,  setProcessData} = useContext(ProcessContext);
  const { toggleValve, valves } = useContext(WellContext);
  const [hoveredValve, setHoveredValve] = useState(null);

  const DEFAULT_VALVES = {
    FCV_14001: { mode: "Manual", opening: 0, sp: "", pv: "" },
  };

  const [valveData, setValveData] = useState(() => {
    const saved = localStorage.getItem("PostDesalterHEX1_valveData");
    if (!saved) return DEFAULT_VALVES;
    try {
      const parsed = JSON.parse(saved);
      return {
        FCV_14001: { ...DEFAULT_VALVES.FCV_14001, ...parsed.FCV_14001 },
      };
    } catch {
      return DEFAULT_VALVES;
    }
  });

  /* ✅ Persist */
  useEffect(() => {
    localStorage.setItem("PostDesalterHEX1_valveData", JSON.stringify(valveData));
  }, [valveData]);

  const updateValveData = (tag, field, value) => {
    setValveData(prev => ({
      ...prev,
      [tag]: {
        ...(prev[tag] || DEFAULT_VALVES[tag]),
        [field]: field === "opening" ? Number(value) || 0 : value
      }
    }));
  };

  /* ================= PANEL CONTROL ================= */
  const [openPanels, setOpenPanels] = useState([]);
  const openPanel = (tag) => {
    if (!openPanels.includes(tag)) {
      setOpenPanels(prev => [...prev, tag]);
    }
  };
  const closePanel = (tag) => {
    setOpenPanels(prev => prev.filter(p => p !== tag));
  };

  // ================= PUMP PANEL LOGIC =================
  const [openPumpPanels, setOpenPumpPanels] = useState([]);
  const openPumpPanel = (tag) => {
    if (!openPumpPanels.includes(tag)) {
      setOpenPumpPanels(prev => [...prev, tag]);
    }
  };
  const closePumpPanel = (tag) => {
    setOpenPumpPanels(prev => prev.filter(p => p !== tag));
  };

  const tagStyle = {
    position: "absolute",
    top: -30,
    left: "50%",
    transform: "translateX(-50%)",
    background: "black",
    color: "#00ffcc",
    fontSize: "13px",
    fontWeight: "bold",
    padding: "3px 8px",
    borderRadius: "4px",
    border: "1px solid #555",
    whiteSpace: "nowrap",
    zIndex: 999
  };

  // ================= RETREIVING OF DATA FROM OTHER PAGES VIA LOCAL STROAGE =================
  const { transmitters } = usePlantStore();
  const TT_15010 = transmitters.TT_15010 || 0;
  const TT_13001 = transmitters.TT_13001 || 0;
  const TT_24013 = transmitters.TT_24013 || 0;

  const xv14001Open = !valves?.XV_14001;
  const xv14002Open = !valves?.XV_14002;
  const xv14003Open = !valves?.XV_14003;

  const TT_14001 = TT_13001 * 0.985;
  const TT_14002 = xv14001Open ? TT_15010 : 23; // TT_15010 (ADD THIS LATER)
  const TT_14003 = xv14002Open ? TT_24013 : 23;
  const TT_14004 = xv14003Open ? 260.347 : 23;

  
  
  
  /* =========================================================
   E-105A/B CRUDE ↔ DIESEL HEAT EXCHANGER
========================================================= */

const TT_14005 = processData.TT_14005;
const TT_14006 = processData.TT_14006;

/* =========================================================
   REFS
========================================================= */

const E105_intervalRef = useRef(null);
const E105_isMounted = useRef(false);

const TT_14005_Ref =
  useRef(processData.TT_14005 ?? 140);

const TT_14006_Ref =
  useRef(processData.TT_14006 ?? 95);

const E105_processRef =
  useRef({});

/* =========================================================
   KEEP PROCESS UPDATED
========================================================= */

useEffect(() => {

  E105_processRef.current = {

    TT_14001,
    TT_14002

  };

});

/* =========================================================
   KEEP REFS UPDATED
========================================================= */

useEffect(() => {

  TT_14005_Ref.current =
    TT_14005 ?? TT_14005_Ref.current;

}, [TT_14005]);

useEffect(() => {

  TT_14006_Ref.current =
    TT_14006 ?? TT_14006_Ref.current;

}, [TT_14006]);

/* =========================================================
   E-105 HEAT EXCHANGER
========================================================= */

useEffect(() => {

  if (E105_isMounted.current)
    return;

  E105_isMounted.current = true;

  console.log(
    "✅ E-105 interval created"
  );

  E105_intervalRef.current =
    setInterval(() => {

      const {

        TT_14001,
        TT_14002

      } = E105_processRef.current;

      const crudeInlet =
        Number(TT_14001) || 0;

      const dieselInlet =
        Number(TT_14002) || 0;

      let crudeOutlet =
        crudeInlet;

      let dieselOutlet =
        dieselInlet;

      const hotter =
        Math.max(
          crudeInlet,
          dieselInlet
        );

      const colder =
        Math.min(
          crudeInlet,
          dieselInlet
        );

      const deltaT =
        hotter - colder;

      if (deltaT > 0.05) {

        /*
          Two exchangers in series
        */

        const effectiveness =
          0.30;

        const duty =
          Math.max(
            0.50,
            deltaT *
            effectiveness
          );

        const secondaryDuty =
          duty * 0.72;

        if (
          dieselInlet >= crudeInlet
        ) {

          dieselOutlet =
            dieselInlet -
            duty;

          crudeOutlet =
            crudeInlet +
            secondaryDuty;

          dieselOutlet =
            Math.max(
              dieselOutlet,
              crudeInlet + 0.5
            );

          crudeOutlet =
            Math.min(
              crudeOutlet,
              dieselInlet - 0.5
            );

        }

        else {

          crudeOutlet =
            crudeInlet -
            duty;

          dieselOutlet =
            dieselInlet +
            secondaryDuty;

          crudeOutlet =
            Math.max(
              crudeOutlet,
              dieselInlet + 0.5
            );

          dieselOutlet =
            Math.min(
              dieselOutlet,
              crudeInlet - 0.5
            );

        }

      }

      /* =====================================
         THERMAL LAG
      ===================================== */

      TT_14005_Ref.current +=

        (
          crudeOutlet -
          TT_14005_Ref.current
        ) * 0.08;

      TT_14006_Ref.current +=

        (
          dieselOutlet -
          TT_14006_Ref.current
        ) * 0.08;

      /* =====================================
         UPDATE PROCESS CONTEXT
      ===================================== */

      setProcessData(prev => ({

        ...prev,

        TT_14005: Number(
          TT_14005_Ref.current.toFixed(2)
        ),

        TT_14006: Number(
          TT_14006_Ref.current.toFixed(2)
        )

      }));

    }, 1000);

  return () => {

    console.log(
      "❌ E-105 interval cleared"
    );

    if (
      E105_intervalRef.current
    ) {

      clearInterval(
        E105_intervalRef.current
      );

      E105_intervalRef.current =
        null;

    }

    E105_isMounted.current =
      false;

  };

}, []);







/* =========================================================
   E-106A/B CRUDE ↔ KEROSENE HEAT EXCHANGER
========================================================= */

const TT_14007 = processData.TT_14007;
const TT_14008 = processData.TT_14008;

/* =========================================================
   REFS
========================================================= */

const E106_intervalRef = useRef(null);
const E106_isMounted = useRef(false);

const TT_14007_Ref =
  useRef(processData.TT_14007 ?? 120);

const TT_14008_Ref =
  useRef(processData.TT_14008 ?? 95);

const E106_processRef =
  useRef({});

/* =========================================================
   KEEP PROCESS UPDATED
========================================================= */

useEffect(() => {

  E106_processRef.current = {

    TT_14003,
    TT_14006

  };

});

/* =========================================================
   KEEP REFS UPDATED
========================================================= */

useEffect(() => {

  TT_14007_Ref.current =
    TT_14007 ?? TT_14007_Ref.current;

}, [TT_14007]);

useEffect(() => {

  TT_14008_Ref.current =
    TT_14008 ?? TT_14008_Ref.current;

}, [TT_14008]);

/* =========================================================
   E-106 HEAT EXCHANGER
========================================================= */

useEffect(() => {

  if (E106_isMounted.current)
    return;

  E106_isMounted.current = true;

  console.log(
    "✅ E-106 interval created"
  );

  E106_intervalRef.current =
    setInterval(() => {

      const {

        TT_14003,
        TT_14006

      } = E106_processRef.current;

      const keroseneInlet =
        Number(TT_14003) || 0;

      const crudeInlet =
        Number(TT_14006) || 0;

      let crudeOutlet =
        crudeInlet;

      let keroseneOutlet =
        keroseneInlet;

      const hotter =
        Math.max(
          keroseneInlet,
          crudeInlet
        );

      const colder =
        Math.min(
          keroseneInlet,
          crudeInlet
        );

      const deltaT =
        hotter - colder;

      if (deltaT > 0.05) {

        /*
          Two exchangers in series
          Lower effectiveness
        */

        const effectiveness =
          0.18;

        const duty =
          Math.max(
            0.30,
            deltaT *
            effectiveness
          );

        const secondaryDuty =
          duty * 0.35;

        if (
          keroseneInlet >= crudeInlet
        ) {

          keroseneOutlet =
            keroseneInlet -
            duty;

          crudeOutlet =
            crudeInlet +
            secondaryDuty;

          keroseneOutlet =
            Math.max(
              keroseneOutlet,
              crudeInlet + 0.5
            );

          crudeOutlet =
            Math.min(
              crudeOutlet,
              keroseneInlet - 0.5
            );

        }

        else {

          crudeOutlet =
            crudeInlet -
            duty;

          keroseneOutlet =
            keroseneInlet +
            secondaryDuty;

          crudeOutlet =
            Math.max(
              crudeOutlet,
              keroseneInlet + 0.5
            );

          keroseneOutlet =
            Math.min(
              keroseneOutlet,
              crudeInlet - 0.5
            );

        }

      }

      /* =====================================
         THERMAL LAG
      ===================================== */

      TT_14007_Ref.current +=

        (
          crudeOutlet -
          TT_14007_Ref.current
        ) * 0.08;

      TT_14008_Ref.current +=

        (
          keroseneOutlet -
          TT_14008_Ref.current
        ) * 0.08;

      /* =====================================
         UPDATE PROCESS CONTEXT
      ===================================== */

      setProcessData(prev => ({

        ...prev,

        TT_14007: Number(
          TT_14007_Ref.current.toFixed(2)
        ),

        TT_14008: Number(
          TT_14008_Ref.current.toFixed(2)
        )

      }));

    }, 1000);

  return () => {

    console.log(
      "❌ E-106 interval cleared"
    );

    if (
      E106_intervalRef.current
    ) {

      clearInterval(
        E106_intervalRef.current
      );

      E106_intervalRef.current =
        null;

    }

    E106_isMounted.current =
      false;

  };

}, []);



/* =========================================================
   E-107A/B CRUDE ↔ RESIDUE HEAT EXCHANGER
========================================================= */

const TT_14009 = processData.TT_14009;
const TT_14010 = processData.TT_14010;

/* =========================================================
   REFS
========================================================= */

const E107_intervalRef = useRef(null);
const E107_isMounted = useRef(false);

const TT_14009_Ref =
  useRef(processData.TT_14009 ?? 145);

const TT_14010_Ref =
  useRef(processData.TT_14010 ?? 120);

const E107_processRef =
  useRef({});

/* =========================================================
   KEEP PROCESS UPDATED
========================================================= */

useEffect(() => {

  E107_processRef.current = {

    TT_14004,
    TT_14008

  };

});

/* =========================================================
   KEEP REFS UPDATED
========================================================= */

useEffect(() => {

  TT_14009_Ref.current =
    TT_14009 ?? TT_14009_Ref.current;

}, [TT_14009]);

useEffect(() => {

  TT_14010_Ref.current =
    TT_14010 ?? TT_14010_Ref.current;

}, [TT_14010]);

/* =========================================================
   E-107 HEAT EXCHANGER
========================================================= */

useEffect(() => {

  if (E107_isMounted.current)
    return;

  E107_isMounted.current = true;

  console.log(
    "✅ E-107 interval created"
  );

  E107_intervalRef.current =
    setInterval(() => {

      const {

        TT_14004,
        TT_14008

      } = E107_processRef.current;

      const residueInlet =
        Number(TT_14004) || 0;

      const crudeInlet =
        Number(TT_14008) || 0;

      let crudeOutlet =
        crudeInlet;

      let residueOutlet =
        residueInlet;

      const hotter =
        Math.max(
          residueInlet,
          crudeInlet
        );

      const colder =
        Math.min(
          residueInlet,
          crudeInlet
        );

      const deltaT =
        hotter - colder;

      if (deltaT > 0.05) {

        /*
          Final crude preheat exchanger.
          Highest recovery in the train.
        */

        const effectiveness =
          0.34;

        const duty =
          Math.max(
            0.60,
            deltaT *
            effectiveness
          );

        const secondaryDuty =
          duty * 0.72;

        if (
          residueInlet >= crudeInlet
        ) {

          residueOutlet =
            residueInlet -
            duty;

          crudeOutlet =
            crudeInlet +
            secondaryDuty;

          residueOutlet =
            Math.max(
              residueOutlet,
              crudeInlet + 0.5
            );

          crudeOutlet =
            Math.min(
              crudeOutlet,
              residueInlet - 0.5
            );

        }

        else {

          crudeOutlet =
            crudeInlet -
            duty;

          residueOutlet =
            residueInlet +
            secondaryDuty;

          crudeOutlet =
            Math.max(
              crudeOutlet,
              residueInlet + 0.5
            );

          residueOutlet =
            Math.min(
              residueOutlet,
              crudeInlet - 0.5
            );

        }

      }

      /* =====================================
         THERMAL LAG
      ===================================== */

      TT_14009_Ref.current +=

        (
          crudeOutlet -
          TT_14009_Ref.current
        ) * 0.08;

      TT_14010_Ref.current +=

        (
          residueOutlet -
          TT_14010_Ref.current
        ) * 0.08;

      /* =====================================
         UPDATE PROCESS CONTEXT
      ===================================== */

      setProcessData(prev => ({

        ...prev,

        TT_14009: Number(
          TT_14009_Ref.current.toFixed(2)
        ),

        TT_14010: Number(
          TT_14010_Ref.current.toFixed(2)
        )

      }));

    }, 1000);

  return () => {

    console.log(
      "❌ E-107 interval cleared"
    );

    if (
      E107_intervalRef.current
    ) {

      clearInterval(
        E107_intervalRef.current
      );

      E107_intervalRef.current =
        null;

    }

    E107_isMounted.current =
      false;

  };

}, []);







  // ✅ Update PlantStore without causing re-renders
  useEffect(() => {
    const current = PlantStore.get().transmitters;
    if (
      current.TT_14009 === TT_14009 &&
      current.TT_14007 === TT_14007 &&
      current.TT_14005 === TT_14005 &&
      current.TT_14003 === TT_14003 &&
      current.TT_14010 === TT_14010
    ) {
      return;
    }

    PlantStore.set({
      transmitters: {
        ...current,
        TT_14009,
        TT_14007,
        TT_14005,
        TT_14010, TT_14003
      }
    });
  }, [TT_14009, TT_14007, TT_14005, TT_14010, TT_14003]);

  return (
    <div className="PostDesalterHEX1-container">
      {/* TITLE */}
      <h2 style={{
        position: "absolute",
        top: "0px",
        left: "240px",
        color: "white",
        background: "rgb(30, 5, 250)",
        padding: "2px 273px",
        zIndex: 10,
      }}>
        POST DESALTER HEATERS 1
      </h2>

      {/* =================SHUTDOWN VALVES ================= */}
      <Valve
        className="XV-14001"
        tag="XV_14001"
        x={227}
        y={298}
        isClosed={valves.XV_14001}
        toggle={toggleValve}
      />

      <Valve
        className="XV-14002"
        tag="XV_14002"
        x={1083}
        y={114}
        isClosed={valves.XV_14002}
        toggle={toggleValve}
      />

      <Valve
        className="XV-14003"
        tag="XV_14003"
        x={1047}
        y={633}
        isClosed={valves.XV_14003}
        toggle={toggleValve}
      />

      {/* ================= CONTROL VALVE FACEPLATES ================= */}
      {openPanels.map((tag, index) => (
        <ControlValveControlPanel
          key={tag}
          tag={tag}
          data={valveData?.[tag] ?? DEFAULT_VALVES?.[tag]}
          update={updateValveData}
          close={() => closePanel(tag)}
          defaultPosition={{
            x: 300 + index * 40,
            y: 150 + index * 40
          }}
        />
      ))}

      {/* ================= PUMPS CONTROL VALVE FACEPLATES ================= */}
      {openPumpPanels.map((tag, index) => (
        <PumpFaceplate
          key={tag}
          tag={tag}
          close={() => closePumpPanel(tag)}
          defaultPosition={{
            x: 300 + index * 40,
            y: 150 + index * 40
          }}
        />
      ))}

      {/* ================= TRANSMITTERS ================= */}
      <Transmitter
        tag="TT_14001"
        value={[TT_14001]}
        x={122}
        y={625}
        unit="°C"
        max={200}
        HH={186}
        H={165}
        L={0}
        LL={0}
        c="lime"
        description="TEMPERATURE OF CRUDE FROM DESALTER UNIT V-201B TO POST DESALTER HEX AT INLET OF E-105A/D"
      />

      <Transmitter
        tag="TT_14002"
        value={[TT_14002]}
        x={122}
        y={280}
        unit="°C"
        max={300}
        HH={286}
        H={265}
        L={0}
        LL={0}
        c="lime"
        description="TEMPERATURE OF DIESEL FROM E-109A/B TO INLET OF E-105B IN POST DESALTER HEX 1"
      />

      <Transmitter
        tag="TT_14003"
        value={[TT_14003]}
        x={1142}
        y={100}
        unit="°C"
        max={300}
        HH={286}
        H={265}
        L={0}
        LL={0}
        c="lime"
        description="TEMPERATURE OF KEROSENE FROM E-105A/B TO INLET OF E-106B IN POST DESALTER HEX 1"
      />

      <Transmitter
        tag="TT_14004"
        value={[TT_14004]}
        x={1142}
        y={615}
        unit="°C"
        max={300}
        HH={286}
        H={265}
        L={0}
        LL={0}
        c="lime"
        description="TEMPERATURE OF ATM RESIDUE FROM E-111A/B TO INLET OF E-107B IN POST DESALTER HEX 1"
      />

      <Transmitter
        tag="TT_14005"
        value={[TT_14005]}
        x={122}
        y={695}
        unit="°C"
        max={300}
        HH={286}
        H={265}
        L={0}
        LL={0}
        c="lime"
        description="TEMPERATURE OF DIESEL FROM E-105A/B TO E-103A/B IN PREHEAT TRAIN 2"
      />

      <Transmitter
        tag="TT_14006"
        value={[TT_14006]}
        x={429}
        y={265}
        unit="°C"
        max={300}
        HH={286}
        H={265}
        L={0}
        LL={0}
        c="lime"
        description="TEMPERATURE OF CRUDE FROM E-105A/B TO E-106A/B"
      />

      <Transmitter
        tag="TT_14007"
        value={[TT_14007]}
        x={252}
        y={205}
        unit="°C"
        max={300}
        HH={286}
        H={265}
        L={0}
        LL={0}
        c="lime"
        description="TEMPERATURE OF KEROSENE FROM E-106A/B TO E-102A/B IN PREHEAT TRAIN 1"
      />

      <Transmitter
        tag="TT_14008"
        value={[TT_14008]}
        x={826}
        y={299}
        unit="°C"
        max={300}
        HH={286}
        H={265}
        L={0}
        LL={0}
        c="lime"
        description="TEMPERATURE OF CRUDE FROM E-106A/B TO E-107A/B"
      />

      <Transmitter
        tag="TT_14009"
        value={[TT_14009]}
        x={1115}
        y={265}
        unit="°C"
        max={300}
        HH={286}
        H={265}
        L={0}
        LL={0}
        c="lime"
        description="TEMPERATURE OF ATM RESIDUE FROM E-107A/B TO E-104A/B IN PREHEAT TRAIN 2"
      />

      <Transmitter
        tag="TT_14010"
        value={[TT_14010]}
        x={815}
        y={685}
        unit="°C"
        max={300}
        HH={286}
        H={265}
        L={0}
        LL={0}
        c="lime"
        description="TEMPERATURE OF CRUDE FROM E-107A/B TO E-108A/D"
      />
    </div>
  );
}

export default memo(PostDesalterHEX1);